import { coverSrc } from "./cover";
import INSTITUTIONS from "./institutions.json";

/**
 * One content model for Journal entries and Impact pages.
 *
 * Every entry is a markdown doc with this frontmatter (the admin writes
 * exactly these keys; older keys are still read, see LEGACY):
 *
 *   title          required
 *   date           required, YYYY-MM-DD
 *   category       required, ONE label: shown on the card, used by filters
 *   description    required, 1–2 sentences: card summary and meta description
 *   image          required cover (+ imageAlt, imageCaption)
 *   tags           optional keywords: article footer and search engines
 *   institution    impact only: who the work was for (drives the URL)
 *   pinned         impact only: shown as a case study line
 *   status, slug, lang
 *
 * Reading time is computed from the body, never typed by hand. The key order
 * written to disk is EDITABLE_KEYS; legacy keys never written are DROPPED_KEYS.
 *
 * LEGACY → current: tag → category (impact cards always showed `tag`),
 * excerpt → description, featured/subtitle/client/year/org → dropped
 * (nothing reads them; year comes from date, org from institution).
 */

/** Display name of each institution key. */
// JSON so the build scripts (CommonJS) read the same list.
export const ORG_NAMES = INSTITUTIONS;

/** Frontmatter keys the admin writes, in the order they are written. */
export const EDITABLE_KEYS = [
	"title",
	"slug",
	"date",
	"category",
	"description",
	"image",
	"imageAlt",
	"imageCaption",
	"tags",
	"institution",
	"pinned",
	"lang",
	"status",
];

/** Legacy keys that are read but never written back. */
export const DROPPED_KEYS = [
	"tag",
	"excerpt",
	"featured",
	"subtitle",
	"client",
	"year",
	"org",
	"readingTime",
];

const WORDS_PER_MINUTE = 220;

/** Minutes to read a markdown body, at least 1. */
export function readingMinutes(body = "") {
	const text = String(body)
		.replace(/```[\s\S]*?```/g, " ")
		.replace(/<!--[\s\S]*?-->/g, " ")
		.replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
		.replace(/[#>*_`|[\]()-]/g, " ");
	const words = text.split(/\s+/).filter(Boolean).length;
	return Math.max(1, Math.round(words / WORDS_PER_MINUTE));
}

const asBool = (v) => v === true || v === "true";
const asList = (v) => {
	if (Array.isArray(v)) return v.filter(Boolean);
	if (!v) return [];
	try {
		const parsed = JSON.parse(v);
		if (Array.isArray(parsed)) return parsed;
	} catch {}
	return String(v)
		.replace(/[[\]"]/g, "")
		.split(",")
		.map((s) => s.trim())
		.filter(Boolean);
};

/** File slug of an Impact page: `<institution>-<page>`. */
export const impactSlug = (institution, page) => `${institution}-${page}`;

const KNOWN_INSTITUTIONS = Object.keys(ORG_NAMES).sort((a, b) => b.length - a.length);

/**
 * Split an Impact file slug into institution and page. A known institution
 * key wins (so "radio-france-rag" is radio-france / rag), else the first
 * segment; `institution`, when the frontmatter declares one, is trusted.
 * @param {string} slug
 * @param {string} [institution]
 * @returns {{ institution: string, page: string }} page falls back to the whole slug.
 */
export function splitImpactSlug(slug, institution = "") {
	const s = String(slug);
	const inst =
		institution || KNOWN_INSTITUTIONS.find((k) => s.startsWith(`${k}-`)) || s.split("-")[0];
	return { institution: inst, page: s.slice(inst.length + 1) || s };
}

/**
 * The site's view of one entry, whatever keys its file happens to use.
 * @param {"journal" | "impact"} type
 * @param {string} slug file slug
 * @param {object} [meta] frontmatter values (strings, or typed by parseEntry)
 * @param {string} [body] markdown body, used for reading time
 */
export function normalizeEntry(type, slug, meta = {}, body = "") {
	const impact = type === "impact" ? splitImpactSlug(slug, meta.institution) : null;
	const institution = impact ? impact.institution : null;
	const date = meta.date || "";
	return {
		type,
		slug,
		title: meta.title || slug,
		date,
		year: String(date || meta.year || "").slice(0, 4),
		category: (type === "impact" ? meta.tag || meta.category : meta.category || meta.tag) || "",
		description: meta.description || meta.excerpt || "",
		tags: asList(meta.tags),
		image: coverSrc(type, meta.image),
		imageAlt: meta.imageAlt || meta.title || "",
		imageCaption: meta.imageCaption || "",
		readingMinutes: readingMinutes(body),
		lang: meta.lang || "",
		status: meta.status || "",
		institution,
		org: institution ? ORG_NAMES[institution] || institution : "",
		pinned: type === "impact" && asBool(meta.pinned),
		href: impact ? `/impact/${impact.institution}/${impact.page}` : `/journal/${slug}`,
	};
}

/**
 * Frontmatter the admin form edits, pre-filled from any legacy file; a
 * missing date defaults to today.
 * @param {"journal" | "impact"} type
 * @param {string} slug
 * @param {object} [meta]
 * @param {{ now?: () => Date }} [deps]
 */
export function editableFields(type, slug, meta = {}, { now = () => new Date() } = {}) {
	const n = normalizeEntry(type, slug, meta, "");
	return {
		title: meta.title || "",
		slug: meta.slug || slug,
		date: meta.date || now().toISOString().split("T")[0],
		category: n.category,
		description: n.description,
		tags: n.tags,
		image: meta.image || "",
		imageAlt: meta.imageAlt || "",
		imageCaption: meta.imageCaption || "",
		lang: meta.lang || "",
		status: meta.status || "draft",
		...(type === "impact" ? { institution: n.institution, pinned: n.pinned } : {}),
	};
}
